<script setup lang="ts">
import type { IStaffDepartmentTreeNode } from '../../types/settings/IStaffDepartmentSettings'
import StaffSettingsEntityRow from './StaffSettingsEntityRow.vue'

defineProps<{
  node: IStaffDepartmentTreeNode
  depth?: number
  selectedId: string | null
  collapsedIds: string[]
  dragEnabled: boolean
  editingId: string | null
  draftName: string
  dragOverId: string | null
  search?: string
}>()

const emit = defineEmits<{
  select: [id: string]
  toggle: [id: string]
  'update:draftName': [value: string]
  save: []
  rename: [id: string, name: string]
  remove: [id: string]
  dragstart: [id: string, event: DragEvent]
  dragover: [id: string]
  drop: [id: string, event: DragEvent]
}>()
</script>

<template>
  <div class="staff-tree-node">
    <StaffSettingsEntityRow
      :name="node.name"
      :count="node.employee_count ?? 0"
      :selected="selectedId === String(node.id)"
      :editing="editingId === String(node.id)"
      :draft="draftName"
      :highlight="search"
      :indent="12 + (depth || 0) * 16"
      menu="edit"
      :draggable="dragEnabled && editingId !== String(node.id)"
      :drop-enabled="dragEnabled"
      :drag-over="dragOverId === String(node.id)"
      @select="emit('select', String(node.id))"
      @update:draft="emit('update:draftName', $event)"
      @save="emit('save')"
      @action="emit('rename', String(node.id), node.name)"
      @remove="emit('remove', String(node.id))"
      @dragstart="emit('dragstart', String(node.id), $event)"
      @dragover="emit('dragover', String(node.id))"
      @drop="emit('drop', String(node.id), $event)"
    >
      <template #prepend>
        <VIcon
          v-if="node.children?.length"
          :icon="collapsedIds.includes(String(node.id)) ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'"
          size="20"
          class="staff-tree-icon flex-shrink-0"
          :class="{ 'text-primary': selectedId === String(node.id) }"
          @click.stop="emit('toggle', String(node.id))"
        />
        <span v-else class="staff-tree-placeholder" />
      </template>
    </StaffSettingsEntityRow>
    <template v-if="node.children?.length && !collapsedIds.includes(String(node.id))">
      <DepartmentTreeNode
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="(depth || 0) + 1"
        :selected-id="selectedId"
        :collapsed-ids="collapsedIds"
        :drag-enabled="dragEnabled"
        :editing-id="editingId"
        :draft-name="draftName"
        :drag-over-id="dragOverId"
        :search="search"
        @select="emit('select', $event)"
        @toggle="emit('toggle', $event)"
        @update:draft-name="emit('update:draftName', $event)"
        @save="emit('save')"
        @rename="(id, name) => emit('rename', id, name)"
        @remove="emit('remove', $event)"
        @dragstart="(id, event) => emit('dragstart', id, event)"
        @dragover="emit('dragover', $event)"
        @drop="(id, event) => emit('drop', id, event)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.staff-tree-icon {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.staff-tree-placeholder {
  flex-shrink: 0;
  inline-size: 20px;
}
</style>
